import { useTranslation } from '../../i18n';
import React from 'react';
import { C } from '../../constants/theme';
import { useClinicDoctors } from '../../hooks/useClinicDoctors';
import { bffMutation } from '../../lib/bffClient';
import {
  ADD_PATIENT, SET_PATIENT_POLICY, CREATE_APPOINTMENT, CREATE_APPOINTMENT_NOTIFYING,
} from '../Booking/constants';
import { notificationSummary } from '../../lib/appointmentNotifications';
import { phoneFlags, phoneRegistrationError } from '../../lib/patient';
import { doctorColor, buildDoctorSlots } from '../Booking/helpers';
import BookingModal from '../Booking/components/BookingModal';
import { fmtSlotLabel } from '../Booking/components/FirstSlotPicker';
import { useShortcut } from '../../lib/shortcuts.jsx';
import PractitionerPickerModal from './PractitionerPickerModal';
import ConnectDoctorModal from './ConnectDoctorModal';
import { bffQuery } from '../../lib/bffClient';
import { REQUESTS_QUERY } from '../../hooks/useAppointmentRequests';

// «تأیید با زمان دیگر» commits the EXISTING row rather than creating a second one
// beside the request the patient is still waiting on.
const CONFIRM_REQUEST = `
  mutation ConfirmAppointmentRequest($appointmentId: ID!, $newDateTime: String) {
    confirmAppointmentRequest(appointmentId: $appointmentId, newDateTime: $newDateTime) { id status dateTime }
  }
`;

// «ویرایش نوبت» (PM-0102). Two documents for the same reason booking has two: only
// a move that asked for a notice selects what each channel did.
const RESCHEDULE_APPOINTMENT = `
  mutation RescheduleAppointment($appointmentId: ID!, $newDateTime: String!) {
    rescheduleAppointment(appointmentId: $appointmentId, newDateTime: $newDateTime) { id status dateTime }
  }
`;
const RESCHEDULE_APPOINTMENT_NOTIFYING = `
  mutation RescheduleAppointmentNotifying($appointmentId: ID!, $newDateTime: String!, $notifyChannels: [String!]) {
    rescheduleAppointment(appointmentId: $appointmentId, newDateTime: $newDateTime, notifyChannels: $notifyChannels) {
      id status dateTime
      notifications { channel status reason detail cost }
    }
  }
`;
const SET_APPOINTMENT_NOTE = `
  mutation SetAppointmentNote($appointmentId: ID!, $note: String!) {
    setAppointmentNote(appointmentId: $appointmentId, note: $note) { id reminderNote }
  }
`;

// «ثبت نوبت» from the day panel. The day panel has no doctor context of its own,
// so this wrapper adds the one step BookingModal lacks — picking the doctor —
// and then defers everything else (patient search / new patient / quick mode,
// presence-filtered slots) to the exact same modal the clinic calendar uses.
// `presetDoctorId` skips the picker (the «+ نوبت» button on a doctor's section).
//
// `initialDate` is the day the panel is showing. Booking from a day you have navigated
// to should land on THAT day: opening the modal on today, while next Tuesday fills the
// screen behind it, is how you book a patient into the wrong week.
export default function BookAppointmentModal({
  presetDoctorId, initialDate, onClose, onBooked,
  // An AppointmentRequest row (from `appointmentRequests`). Present ⇒ this modal
  // is «تأیید با زمان دیگر»: the practitioner and the patient are already
  // decided, only the time is open, and committing CONFIRMS the request.
  confirmRequest = null,
  // ── Opened PREFILLED by «منشی هوشمند» (src/secretary/SecretaryHost.jsx) ──────
  // `presetPatient` ({ id, name, phone }) is a patient already decided, shown as
  // the same identity line a confirmation shows; `initialSlotLabel` ("HH:MM" on
  // the clinic's clock) pre-selects a time on `initialDate`; `notice` is rendered
  // above the form — it is where the «پیشنهاد هوش مصنوعی» statement lives. This is
  // still an ordinary booking: the desk can change anything, and submit is the
  // same createAppointment as always.
  presetPatient = null,
  // `presetNewPatient` ({ name }) is a patient the clinic has NO record of — the
  // secretary heard a name nobody here has. The form opens on «بیمار جدید» with the
  // name filled in and the mobile next; its own submit registers them, as always.
  presetNewPatient = null,
  initialSlotLabel = null,
  notice = null,
  // ── «ویرایش نوبت» (PM-0102) ────────────────────────────────────────────────
  // `{ appointment: { id, doctorId, doctorName, dateTime, time, note }, patient:
  // { id, name, phone } }` — a row of the day list. Present ⇒ this modal EDITS
  // that appointment: the practitioner and the patient are the row's, the time
  // and the note are open, and committing moves the existing row
  // (`rescheduleAppointment`) and/or rewrites its note — never a second نوبت.
  reschedule = null,
}) {
  const { formatDate, formatTime } = useTranslation();
  const { formatDigits, t } = useTranslation();
  const { doctors, loading, acceptDoctor } = useClinicDoctors();

  // Same eligibility rule as the Doctors screen: only accepted, active links are bookable.
  const bookable = React.useMemo(
    () => (doctors || []).filter(d => d.status === 'accepted' && d.active !== false),
    [doctors],
  );
  // Links still waiting for the clinic's approval: the «اتصال پزشک» modal offers
  // to accept them in place, and `bookable` picks the doctor up on the next render.
  const pending = React.useMemo(
    () => (doctors || []).filter(d => d.status && d.status !== 'accepted'),
    [doctors],
  );

  const [docId, setDocId] = React.useState(presetDoctorId || reschedule?.appointment?.doctorId || null);
  const [error, setError] = React.useState('');
  // An `addPatient` refusal because the mobile carries records — handed back to the
  // modal, which owns the «بیمار جدید» form and shows the candidates inline.
  const [phoneConflict, setPhoneConflict] = React.useState(null);

  // ── Slots a patient REQUEST is holding (E8) ────────────────────────────────
  //
  // A `requested` row occupies its slot exactly as a booked one does — that is
  // what «holds its slot» means in §7.1 — so booking over it would be refused by
  // the scheduling service's own overlap check. Read once when the modal opens
  // and handed to BookingModal as a warning rather than a filter: a slot that
  // silently disappears from the grid is a request the desk never learns about.
  const [heldSlots, setHeldSlots] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    bffQuery(REQUESTS_QUERY, { state: 'open', limit: 200 })
      .then((data) => {
        if (!live) return;
        const held = new Map();
        for (const row of data?.appointmentRequests || []) {
          const appt = row?.appointment;
          if (!appt?.dateTime) continue;
          const provider = appt.providerId || appt.doctorId || '';
          if (!held.has(provider)) held.set(provider, new Set());
          held.get(provider).add(String(appt.dateTime));
        }
        setHeldSlots(held);
      })
      // A read we could not make is not «no requests»: it simply means no
      // warning this time. The service still refuses the clash.
      .catch(() => { if (live) setHeldSlots(null); });
    return () => { live = false; };
  }, []);

  // Registered here rather than on a local keydown so it takes precedence over the
  // پذیرش screen's own keys for exactly as long as this modal is mounted.
  useShortcut('esc', onClose, { label: t('common.actions.close'), group: t('reception.actions.book') });

  // An edit is on the row's OWN practitioner, whoever else is bookable — and still
  // on them when their link is no longer active: the نوبت exists, and the service
  // is what decides whether it may move. The picker is never a step of an edit.
  const editedDoctor = reschedule?.appointment?.doctorId
    ? ((doctors || []).find(d => d.doctorId === reschedule.appointment.doctorId)
      || (loading ? null : {
        doctorId: reschedule.appointment.doctorId,
        doctorName: reschedule.appointment.doctorName || '',
        presences: [],
      }))
    : null;
  const selected = reschedule
    ? editedDoctor
    : (bookable.find(d => d.doctorId === docId) ||
      (bookable.length === 1 ? bookable[0] : null));

  // The two writes of an edit, each only when its answer changed. The move goes
  // first: it is the one that can be refused, and a refused edit must not have
  // already rewritten the note.
  async function commitEdit({ dateTimeMs, notes, notifyChannels }) {
    const appt = reschedule.appointment;
    const who = reschedule.patient?.name || t('common.entity.patient');
    const moving = dateTimeMs != null && String(dateTimeMs) !== String(appt.dateTime);
    const note = String(notes || '').trim();
    const noteChanged = note !== String(appt.note || '').trim();
    const said = [];
    if (moving) {
      const notify = reschedule.patient?.id && notifyChannels?.length ? notifyChannels : null;
      const res = await bffMutation(notify ? RESCHEDULE_APPOINTMENT_NOTIFYING : RESCHEDULE_APPOINTMENT, {
        appointmentId: appt.id,
        newDateTime: String(dateTimeMs),
        ...(notify ? { notifyChannels: notify } : {}),
      });
      const moved = res?.rescheduleAppointment;
      if (!moved?.id) throw new Error(t('reception.edit.failed'));
      said.push(t('reception.edit.movedTo', {
        who, when: fmtSlotLabel(moved.dateTime || String(dateTimeMs), t, formatDate, formatTime),
      }));
      const notified = notificationSummary(moved.notifications, t);
      if (notified) said.push(notified);
    }
    if (noteChanged) {
      try {
        await bffMutation(SET_APPOINTMENT_NOTE, { appointmentId: appt.id, note });
        if (!moving) said.push(t('reception.edit.noteSaved', { who }));
      } catch (e) {
        // The move, if there was one, is done and stays done; say which half failed.
        if (!moving) throw e;
        said.push(t('reception.edit.noteFailed'));
      }
    }
    // Nothing changed: close without claiming anything was saved.
    onBooked?.(said.length ? said.join(' · ') : null);
    onClose();
  }

  async function handleSubmit({
    patientInfo, placeholderLabel, dateTimeMs, notes, type, firstAvailable, services,
    notifyChannels, untimed, serviceDeclarationId, insuranceChoice,
  }) {
    setError('');
    try {
      if (reschedule?.appointment?.id) {
        await commitEdit({ dateTimeMs, notes, notifyChannels });
        return;
      }
      // «تأیید با زمان دیگر»: one call, and it is a CONFIRMATION. The row already
      // exists, the patient is already on it, and the service re-validates
      // presence and overlap at the new instant exactly as it did at the first.
      if (confirmRequest?.appointment?.id) {
        const res = await bffMutation(CONFIRM_REQUEST, {
          appointmentId: confirmRequest.appointment.id,
          newDateTime: String(dateTimeMs),
        });
        const confirmed = res?.confirmAppointmentRequest;
        if (!confirmed?.id) throw new Error(t('reception.requests.confirmFailed'));
        onBooked?.(t('reception.requests.confirmedAt', {
          who: confirmRequest.patient?.name || t('common.entity.patient'),
          when: fmtSlotLabel(confirmed.dateTime || String(dateTimeMs), t, formatDate, formatTime),
        }));
        onClose();
        return;
      }
      // «ثبت سریع»: no patient is created — the appointment holds its slot on the label
      // alone, and the file is completed later from the day panel. Otherwise resolve a
      // real patient id, registering the بیمار جدید form if it has no id yet.
      let patientId, labelInput;
      if (placeholderLabel) {
        labelInput = placeholderLabel;
      } else {
        patientId = patientInfo.id;
        if (!patientId) {
          const created = await bffMutation(ADD_PATIENT, {
            input: {
              name:       patientInfo.name,
              phone:      patientInfo.phone      || undefined,
              nationalId: patientInfo.nationalId || undefined,
              fileNumber: patientInfo.fileNumber || undefined,
              ...phoneFlags(patientInfo),
            }
          });
          patientId = created?.addPatient?.id;
          // The insurer is a POLICY, written against the id the creation just
          // returned — `patients.insurance` is derived from it. Best-effort: the
          // patient exists and the slot is about to be held, so a policy that
          // fails to attach must not cost the booking. The proforma the booking
          // raises will price uninsured, and check-in re-prices it.
          if (patientId && patientInfo.insurerId) {
            try {
              await bffMutation(SET_PATIENT_POLICY, {
                input: {
                  patientId,
                  insurerId: patientInfo.insurerId,
                  kind: 'basic',
                  isPrimary: true,
                },
              });
            } catch (e) {
              console.warn('[insurance] policy not attached at booking:', e?.message);
            }
          }
        }
        if (!patientId) throw new Error(t('errors.patients.createFailed'));
      }

      // With `firstAvailable` the dateTime is only an anchor — the server resolves
      // the real slot and returns it, so the toast reports what was actually booked.
      // Only a booking that names a patient AND asked for a notice selects what each
      // channel did — see CREATE_APPOINTMENT_NOTIFYING for why that is a second document.
      const notify = patientId && notifyChannels?.length ? notifyChannels : null;
      const res = await bffMutation(notify ? CREATE_APPOINTMENT_NOTIFYING : CREATE_APPOINTMENT, {
        input: {
          patientId,
          placeholderLabel: labelInput,
          doctorId: selected.doctorId,
          dateTime: String(dateTimeMs),
          type: type || 'in_person',
          reminderNote: notes || undefined,
          firstAvailable: firstAvailable || undefined,
          // «خارج از نوبت» — DAY or NOW. Never sent alongside firstAvailable: the BFF
          // refuses the pair, because one searches for a free hour and this is the
          // booking that has none.
          untimed: untimed || undefined,
          // «خدمت نوبت» — stored ON the appointment and used to price the
          // proforma's primary line, so the invoice bills what was booked
          // (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §D1). Omitted, the
          // BFF resolves the practitioner's own.
          serviceDeclarationId: serviceDeclarationId || undefined,
          // Priced onto the proforma the booking raises (target flow §1–§2).
          services: services && services.length ? services : undefined,
          // This visit's own insurance decisions — which بیمهٔ تکمیلی to skip and
          // which معرفی‌نامه each pays under. Was collected by the modal and then
          // dropped here, so a desk's choice never reached the proforma.
          insuranceChoice: insuranceChoice || undefined,
          notifyChannels: notify || undefined,
        },
      });
      const booked = res?.createAppointment;
      if (!booked?.id) throw new Error(t('errors.reception.bookFailed'));

      const who = placeholderLabel || patientInfo.name || t('common.entity.patient');
      const bookedMsg = booked.dateTime
        ? t('reception.booking.bookedAt', { who, when: fmtSlotLabel(booked.dateTime, t, formatDate, formatTime) })
        : t('reception.booking.booked', { who });
      // What each channel did rides on the same toast: the desk reads one line, and a
      // notice that did not go is something they can still phone about.
      const notified = notificationSummary(booked.notifications, t);
      onBooked?.(notified ? `${bookedMsg} · ${notified}` : bookedMsg);
      onClose();
    } catch (e) {
      const code = e?.extensions?.code;
      const phoneErr = phoneRegistrationError(e);
      if (phoneErr?.code === 'PHONE_HAS_PATIENTS') {
        // Nothing was created. The records go back to the form, which asks the desk.
        setPhoneConflict({ phone: patientInfo?.phone, candidates: phoneErr.candidates });
        setError(t('patients.phoneCandidates.conflictOnSubmit'));
      } else if (phoneErr?.code === 'PHONE_REQUIRED') {
        setError(t('validation.phone.requiredOrNone'));
      } else if (code === 'NO_SLOT_AVAILABLE') {
        setError(firstAvailable === 'ANY'
          ? t('reception.booking.noSlotIn30Days')
          : t('reception.booking.noSlotToday'));
      } else if (code === 'APPOINTMENT_CONFLICT') {
        setError(firstAvailable
          ? t('reception.booking.slotJustTaken')
          : t('reception.booking.doctorBusy'));
      } else {
        setError(e?.message || t(reschedule ? 'reception.edit.failed' : 'errors.reception.bookFailed'));
      }
    }
  }

  // ── Doctor chosen → the shared booking modal, anchored on today ──────────────
  if (selected) {
    const slotMin = selected.visitDurationMinutes || 30;
    return (
      <>
        <BookingModal
          doctor={{
            id: selected.doctorId,
            name: selected.doctorName || t('doctors.placeholder.unnamed', { id: (selected.doctorId || '').slice(0, 6) }),
            color: doctorColor(selected.doctorId),
            specialty: t('doctors.visitDuration', { minutes: formatDigits(slotMin) }),
            presences: selected.presences || []
          }}
          initialDate={reschedule
            ? new Date(Number(reschedule.appointment.dateTime) || Date.now())
            : (initialDate ? new Date(initialDate) : new Date())}
          initialSlotLabel={confirmRequest ? null : (reschedule ? reschedule.appointment.time || null : initialSlotLabel)}
          notice={reschedule ? (
            <div
              data-testid="edit-appointment-current"
              style={{
                padding: '9px 12px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.8,
                background: C.bg, color: C.textSecondary, border: `1px solid ${C.border}`,
              }}
            >
              {t('reception.edit.currentTime', {
                when: fmtSlotLabel(reschedule.appointment.dateTime, t, formatDate, formatTime),
              })}
              <br />
              {t('reception.edit.hint')}
            </div>
          ) : notice}
          timeSlots={buildDoctorSlots(slotMin)}
          /* Auto-pick contradicts a confirmation: the patient asked for a time
             and the desk is choosing another one deliberately, not letting the
             server pick. An edit is the same deliberate choice. */
          allowFirstAvailable={!confirmRequest && !reschedule}
          lockedPatient={reschedule ? {
            id: reschedule.patient?.id || null,
            name: reschedule.patient?.name || '',
            phone: reschedule.patient?.phone || '',
          } : confirmRequest ? {
            id: confirmRequest.patient?.id || confirmRequest.appointment?.patientId,
            name: confirmRequest.patient?.name || '',
            phone: confirmRequest.patient?.phone || '',
          } : (presetPatient?.id ? presetPatient : null)}
          presetNewPatient={!reschedule && !confirmRequest ? presetNewPatient : null}
          editing={reschedule ? {
            appointmentId: reschedule.appointment.id,
            note: reschedule.appointment.note || '',
          } : null}
          submitLabel={reschedule ? t('reception.edit.save') : confirmRequest ? t('reception.requests.confirm') : null}
          /* A «ثبت سریع» row has nobody to tell that its hour moved. */
          offerNotification={reschedule ? !!reschedule.patient?.id : !confirmRequest}
          /* The request being confirmed holds its OWN slot; warning the desk
             about it would be warning them about the row they are moving. */
          heldSlots={heldSlots
            ? new Set([...(heldSlots.get(selected.doctorId) || [])]
              .filter((ms) => ms !== String(confirmRequest?.appointment?.dateTime || '')))
            : null}
          phoneConflict={phoneConflict}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
        {error && (
          <div style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9000, background: C.red, color: '#fff', borderRadius: 12,
            padding: '11px 18px', fontSize: 13, fontWeight: 700, fontFamily: 'Meshkat, sans-serif', boxShadow: '0 12px 34px rgba(0,0,0,.3)'
          }}>
            {error}
          </div>
        )}
      </>
    );
  }

  // An edit whose roster is still loading: nothing to pick, so nothing to show yet.
  if (reschedule) return null;

  // ── No doctor to book for → connect one, instead of a dead-end sentence ──────
  if (!loading && bookable.length === 0) {
    return <ConnectDoctorModal pending={pending} onAccept={acceptDoctor} onClose={onClose} />;
  }

  // ── Doctor picker ─────────────────────────────────────────────────────────────
  return (
    <PractitionerPickerModal
      title={t('reception.actions.book')}
      subtitle={t('reception.booking.pickDoctorFirst')}
      loading={loading}
      loadingLabel={t('reception.booking.loadingDoctors')}
      people={bookable.map((d) => ({
        key: d.doctorId,
        id: d.doctorId,
        name: d.doctorName || t('doctors.placeholder.unnamed', { id: (d.doctorId || '').slice(0, 6) }),
        meta: `${t('common.entity.visit')} ${formatDigits(d.visitDurationMinutes || 30)} ${t('common.units.minuteLong')}`,
      }))}
      onPick={(p) => setDocId(p.id)}
      onClose={onClose}
    />
  );
}
